import React, { useEffect } from 'react'
import { useUser } from '@features/auth/hooks'
import { LanguagesAutocomplete } from '@features/languages/components'
import useAccents from '@features/languages/hooks/use-accent'
import { SubmitHandler, useForm } from 'react-hook-form'
// import { yupResolver } from '@hookform/resolvers/yup'
import { ModuleResult } from '@app/services/auto-generated/auth'
import { useModulesSubmitModuleResultMutation } from '@app/services/auto-generated/modules'
import { Box, Button, Container, MenuItem, Select, Stack, Typography } from '@common/components'
import { SubSettingsContainer } from '@common/containers'

interface FormState {
  accent: {
    code: string
    name: string
    flag_image: string
  } | null
  voiceType: string | null
}

const SubmitAccentForm: React.FC = () => {
  const { user } = useUser()
  const { enhanceAccents, languages, isSuccess: isSuccessLanguages } = useAccents()

  const [submitModule, { isLoading }] = useModulesSubmitModuleResultMutation()
  const {
    handleSubmit,
    control,
    formState: { isDirty },
    getValues,
    reset,
  } = useForm<FormState>({
    defaultValues: {
      accent: enhanceAccents(user?.accent),
      voiceType: user?.voice_gender || null,
    },
    values: {
      accent: enhanceAccents(user?.accent),
      voiceType: user?.voice_gender || null,
    },
    // resolver: yupResolver(updateLanguagesSchema),
  })

  const onSubmit: SubmitHandler<FormState> = async (formState) => {
    try {
      const formData: ModuleResult = {
        id: 'PreferredAccentModule',
        data: {
          preferred_accent: formState.accent?.code,
          preferred_voice_type: formState.voiceType || undefined,
        },
      }
      await submitModule({ moduleResult: formData })
    } catch (error) {
      console.error(error)
      return
    }
  }

  useEffect(() => {
    if (isSuccessLanguages) {
      reset({
        accent: enhanceAccents(user?.accent),
        voiceType: user?.voice_gender || null,
      })
    }
  }, [isSuccessLanguages])

  const currentAccent = getValues('accent')

  return (
    <Box component='form' onSubmit={handleSubmit(onSubmit)}>
      <Typography variant='body1' sx={{ mb: 2 }}>
        Choose an accent
      </Typography>
      <LanguagesAutocomplete
        name='accent'
        options={languages.map((accent) => ({
          code: accent.code,
          name: accent.name,
          flag_img: accent.flag_image,
        }))}
        defaultValue={
          currentAccent
            ? {
                code: currentAccent.code,
                name: currentAccent.name,
                flag_img: currentAccent.flag_image,
              }
            : undefined
        }
        control={control}
        isMultiple={false}
        placeholder={'The accent, I want the feedback in'}
      />
      <Typography variant='body1' sx={{ my: 2 }}>
        Choose feedback voice type
      </Typography>
      <Select name='voiceType' control={control}>
        <MenuItem value='MALE'>Male</MenuItem>
        <MenuItem value='FEMALE'>Female</MenuItem>
      </Select>
      <Stack
        direction='row'
        alignItems='center'
        justifyContent='flex-end'
        spacing={2}
        sx={{ mt: 3 }}
      >
        <Button disabled={!isDirty} variant='outlined' color='neutral' onClick={() => reset({})}>
          cancel
        </Button>
        <Button type='submit' disabled={!isDirty} variant='contained' loading={isLoading}>
          Save Profile
        </Button>
      </Stack>
    </Box>
  )
}

const PreferredAccent = () => {
  return (
    <SubSettingsContainer title='Accent settings'>
      <Container maxWidth='sm'>
        <SubmitAccentForm />
      </Container>
    </SubSettingsContainer>
  )
}

export default PreferredAccent
